import Notification from '../../shared/components/Notification/index';
import { NotificationTypes } from '../../enums/notificationTypes';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import axiosInstance from '../../interceptor/axiosInstance';
import axios, { AxiosError } from 'axios';
import { Sketch, SketchItem } from '../../models/sketch.model';
import { UseMutationOptions, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import { mapPortalServiceMessages } from './mapportal.constants';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';
import { generatePath } from 'react-router-dom';
import type { LayerBounds } from '../../types/CommonMapTypes';

interface CustomAxiosError {
  message?: string;
  error?: { code?: string; message?: string };
}

const getAxiosErrorMessage = (error: unknown): string | undefined => {
  const data = (error as AxiosError<CustomAxiosError>)?.response?.data;
  // Backend returns either `{ message }` or structured `{ error: { code, message } }`.
  return data?.error?.message ?? data?.message;
};

const normalizeSketchItemPayload = (item: SketchItem) => {
  const normalizeField = (value: unknown) => {
    if (typeof value !== 'string') {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  return {
    ...item,
    geojson: normalizeField(item.geojson),
    properties: normalizeField(item.properties),
  };
};

const MapPortalService = () => {
  const createMap = (): UseMutationOptions<any, Error, { mapData: any }> => ({
    mutationKey: [MutationKeys.CREATE_MAP],
    mutationFn: async ({ mapData }) => {
      const { data } = await axiosInstance.post(ApiRoutes.MAP, mapData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.CREATE_MAP.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.CREATE_MAP.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const editMap = (): UseMutationOptions<any, Error, { mapId: string; mapData: any }> => ({
    mutationKey: [MutationKeys.EDIT_MAP],
    mutationFn: async ({ mapId, mapData }) => {
      const endpoint = generatePath(ApiRoutes.MAP_BY_ID, { mapId });
      const { data } = await axiosInstance.put(endpoint, mapData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.EDIT_MAP.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.EDIT_MAP.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const deleteMap = (): UseMutationOptions<boolean, Error, { mapId: string }> => ({
    mutationKey: [MutationKeys.DELETE_MAP],
    mutationFn: async ({ mapId }) => {
      const endpoint = generatePath(ApiRoutes.MAP_BY_ID, { mapId });
      await axiosInstance.delete(endpoint);
      return true;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.DELETE_MAP.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.DELETE_MAP.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const deleteLayer = (): UseMutationOptions<boolean, Error, { mapId: string; layerId: string }> => ({
    mutationKey: [MutationKeys.DELETE_LAYER],
    mutationFn: async ({ mapId, layerId }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LAYER_BY_ID, {
        mapId,
        layerId,
      });
      await axiosInstance.delete(endpoint);
      return true;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.DELETE_LAYER.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.DELETE_LAYER.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getAllMaps = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_ALL_MAPS],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.MAP);
        return response.data;
      } catch (error) {
        Notification({
          message: mapPortalServiceMessages.GET_ALL_MAPS.ERROR,
          description: getAxiosErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const getMapById = (mapId?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_MAP_BY_ID, mapId],
    queryFn: async () => {
      try {
        const endpoint = generatePath(ApiRoutes.MAP_BY_ID, { mapId: String(mapId) });
        const response = await axiosInstance.get(endpoint);
        return response.data;
      } catch (error) {
        Notification({
          message: mapPortalServiceMessages.GET_MAP_BY_ID.ERROR,
          description: getAxiosErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!mapId,
  });

  const getMapLayers = (mapId?: number): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_MAP_LAYERS, mapId],
    queryFn: async () => {
      try {
        const endpoint = generatePath(ApiRoutes.MAP_LAYERS, {
          mapId: String(mapId),
        });
        const response = await axiosInstance.get(endpoint);
        return response.data;
      } catch (error) {
        Notification({
          key: 'get-map-layers-error',
          message: mapPortalServiceMessages.GET_MAP_LAYERS.ERROR,
          description: getAxiosErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!mapId,
  });

  // Symbology FIELD list — column names + numeric flag from the layer schema.
  // Never truncated by feature count, so the dropdown works at any layer size.
  // `isSymbology` marks the call as coming from the symbology panel, opting into
  // the per-field value stats it needs. The query/proximity tools share this
  // endpoint and must not pay for them.
  const getLayerSymbologyFields = (layerName?: string, isSymbology?: boolean): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.SYMBOLOGY_FIELDS, layerName, isSymbology],
    queryFn: async () => {
      const response = await axiosInstance.get(ApiRoutes.SYMBOLOGY_FIELDS, {
        params: { layerName, ...(isSymbology ? { isSymbology: true } : {}) },
      });
      return response.data; // { fields: [{ name, numeric }] }
    },
    enabled: !!layerName,
  });

  // Unique symbology — distinct values of one field (PostGIS DISTINCT, capped).
  const getFieldDistinctValues = (layerName?: string, field?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.SYMBOLOGY_DISTINCT_VALUES, layerName, field],
    queryFn: async () => {
      const response = await axiosInstance.get(ApiRoutes.SYMBOLOGY_DISTINCT_VALUES, {
        params: { layerName, field },
      });
      return response.data; // { values, truncated, limit }
    },
    enabled: !!layerName && !!field,
  });

  // Quantity symbology — exact min/max/count + bounded numeric sample for breaks.
  const getFieldValueSample = (layerName?: string, field?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.SYMBOLOGY_VALUE_SAMPLE, layerName, field],
    queryFn: async () => {
      const response = await axiosInstance.get(ApiRoutes.SYMBOLOGY_VALUE_SAMPLE, {
        params: { layerName, field },
      });
      return response.data; // { values, min, max, count, sampled }
    },
    enabled: !!layerName && !!field,
  });

  // P1 — server-driven attribute table: one page, filtered/sorted/searched in
  // PostGIS, no geometry. keepPreviousData keeps the old page visible while the
  // next loads (no flash). filters: [{field, op, value}].
  const getFeatureTable = (params: {
    layerName?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    search?: string;
    filters?: { field: string; op: string; value: unknown }[];
    // US-103191 query-result Filter ordering, by the COMPACT query predicate
    // (scales to millions — no enumerated id list in the URL). Matching rows
    // float to the top, or sink to the bottom when matchSink is set.
    // matchOrder=false keeps `__is_match` but skips CASE ordering (Display
    // Selection off → natural row order while selection is retained).
    matchFilter?: Record<string, any>;
    matchExtent?: { type: 'Polygon'; coordinates: number[][][] } | null;
    matchSink?: boolean;
    matchOrder?: boolean;
    // Manual selection edits — Display Selection / Reverse order by
    // (match − excluded) ∪ included rather than the raw analysis match alone.
    excludedFeatureIds?: string[];
    includedFeatureIds?: string[];
    // US-103032 proximity result table: order matches (spatial-relationship)
    // first, via a compact predicate config (scales like matchFilter).
    proximityMatch?: Record<string, any> | null;
  }): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_FEATURE_TABLE, params],
    queryFn: async () => {
      const {
        layerName,
        page,
        pageSize,
        sortBy,
        sortDir,
        search,
        filters,
        matchFilter,
        matchExtent,
        matchSink,
        matchOrder,
        proximityMatch,
        excludedFeatureIds,
        includedFeatureIds,
      } = params;
      const response = await axiosInstance.get(ApiRoutes.MAP_FEATURE_TABLE, {
        params: {
          layerName,
          ...(page !== undefined ? { page } : {}),
          ...(pageSize !== undefined ? { pageSize } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortDir ? { sortDir } : {}),
          ...(search ? { search } : {}),
          ...(filters && filters.length ? { filters: JSON.stringify(filters) } : {}),
          ...(matchFilter && Object.keys(matchFilter).length ? { matchFilter: JSON.stringify(matchFilter) } : {}),
          ...(matchExtent ? { matchExtent: JSON.stringify(matchExtent) } : {}),
          ...(matchSink ? { matchSink: true } : {}),
          // Explicit false must be sent so the server skips match-first ordering.
          ...(matchOrder === false ? { matchOrder: false } : {}),
          ...(proximityMatch ? { proximityMatch: JSON.stringify(proximityMatch) } : {}),
          ...(excludedFeatureIds?.length ? { excludedFeatureIds: JSON.stringify(excludedFeatureIds) } : {}),
          ...(includedFeatureIds?.length ? { includedFeatureIds: JSON.stringify(includedFeatureIds) } : {}),
        },
      });
      return response.data; // { rows, total, page, pageSize, columns }
    },
    enabled: !!params.layerName,
    placeholderData: keepPreviousData,
  });

  // Download at scale — kick off an async ogr2ogr export (PostGIS → S3) and
  // return a jobId. Works for the whole layer, a table predicate, a
  // Query/Proximity match predicate, or a gid list.
  const requestFeatureExport = async (body: {
    layerName: string;
    format: 'shapefile' | 'csv' | 'kml' | 'geojson';
    /** Optional display name for the downloaded file (renamed layer title). */
    fileName?: string;
    filter?: { search?: string; filters?: { field: string; op: string; value: unknown }[] };
    featureIds?: string[];
    matchFilter?: Record<string, any>;
    matchExtent?: { type: 'Polygon'; coordinates: number[][][] } | null;
    proximityMatch?: Record<string, any> | null;
    excludedFeatureIds?: string[];
    includedFeatureIds?: string[];
    /** Reverse selection: export the complement of the forward match set. */
    matchInvert?: boolean;
  }): Promise<{ jobId: string; filename: string }> => {
    const response = await axiosInstance.post(ApiRoutes.MAP_EXPORT, body);
    return response.data;
  };

  // Poll one export job. Returns { status, url?, filename?, progress?, error? }.
  const getFeatureExportStatus = async (
    jobId: string,
  ): Promise<{ status: string; url?: string; filename?: string; progress?: number; error?: string }> => {
    const endpoint = generatePath(ApiRoutes.MAP_EXPORT_STATUS, { jobId });
    const response = await axiosInstance.get(endpoint);
    return response.data;
  };

  // P4 — get a CQL_FILTER string for a {search,filters} predicate, used to drive
  // the server-rendered WMS highlight overlay for "select all matching".
  const getFeatureCqlForPredicate = async (
    layerName: string,
    predicate: {
      search?: string;
      filters?: { field: string; op: string; value: unknown }[];
      excluded?: (number | string)[];
    },
  ): Promise<string> => {
    const response = await axiosInstance.get(ApiRoutes.MAP_FEATURE_CQL, {
      params: {
        layerName,
        ...(predicate.search ? { search: predicate.search } : {}),
        ...(predicate.filters && predicate.filters.length ? { filters: JSON.stringify(predicate.filters) } : {}),
        ...(predicate.excluded && predicate.excluded.length ? { excluded: JSON.stringify(predicate.excluded) } : {}),
      },
    });
    return response.data?.cql ?? '';
  };

  // P2 — edit cells (and add columns) by gid, directly in PostGIS. Scales far
  // better than WFS-T for bulk edits and reflects on the map after a GWC truncate.
  const updateFeatureTableRows = (): UseMutationOptions<
    any,
    Error,
    { layerName: string; updates?: { gid: number | string; properties: Record<string, unknown> }[]; newColumns?: any[] }
  > => ({
    mutationKey: [MutationKeys.UPDATE_FEATURE_TABLE_ROWS],
    mutationFn: async ({ layerName, updates, newColumns }) => {
      const endpoint = generatePath(ApiRoutes.MAP_FEATURE_TABLE_UPDATE, { layerName });
      const response = await axiosInstance.patch(endpoint, { updates, newColumns });
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: 'Failed to save changes',
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getLayerBounds = (): UseMutationOptions<any, Error, { layerName: string }> => ({
    mutationKey: [MutationKeys.GET_LAYER_BOUNDS],
    mutationFn: async ({ layerName }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LAYER_BOUNDS, { layerName });
      const response = await axiosInstance.get(endpoint);
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.GET_LAYER_BOUNDS.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  // The layer's live extent from GeoServer, for a camera fit; no toast, because a failed fit just leaves the view as it is.
  const getLayerBoundsQuery = (layerName: string): UseQueryOptions<LayerBounds, Error> => ({
    queryKey: [QueryKeys.GET_LAYER_BOUNDS, layerName],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get(generatePath(ApiRoutes.MAP_LAYER_BOUNDS, { layerName }), { signal });
      return { minX: data?.minX ?? data?.minx, minY: data?.minY ?? data?.miny, maxX: data?.maxX ?? data?.maxx, maxY: data?.maxY ?? data?.maxy };
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const createDuplicateLayer = (): UseMutationOptions<any, Error, { layerName: string; mode?: 'copy' | 'scratch' }> => ({
    mutationKey: [MutationKeys.CREATE_DUPLICATE_LAYER],
    mutationFn: async ({ layerName, mode }) => {
      const endpoint = generatePath(ApiRoutes.DUPLICATE_LAYER, { layerName });
      const response = await axiosInstance.post(endpoint, mode ? { mode } : {});
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.CREATE_DUPLICATE_LAYER.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const downloadLayer = (): UseMutationOptions<any, Error, { layerName: string; format: string }> => ({
    mutationKey: [MutationKeys.DOWNLOAD_LAYER],
    mutationFn: async ({ layerName, format }) => {
      const response = await axiosInstance.get(ApiRoutes.DOWNLOAD_LAYER, {
        params: { layerName, format },
        responseType: 'blob',
      });
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.DOWNLOAD_LAYER.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const downloadSelectedFeatures = (): UseMutationOptions<
    any,
    Error,
    {
      layerName: string;
      format: string;
      featureIds?: string[];
      // P3 — predicate selection ("all matching filter") → exported via CQL_FILTER.
      filter?: { search?: string; filters?: { field: string; op: string; value: unknown }[] };
    }
  > => ({
    mutationKey: [MutationKeys.DOWNLOAD_SELECTED_FEATURES],
    mutationFn: async ({ layerName, format, featureIds, filter }) => {
      const response = await axiosInstance.post(ApiRoutes.DOWNLOAD_FEATURES, filter ? { filter } : { featureIds }, {
        params: { layerName, format },
        responseType: 'blob',
      });
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.DOWNLOAD_SELECTED_FEATURES.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const fetchFeaturesFromLayer = (): UseMutationOptions<
    any,
    Error,
    { layerName: string; maxFeatures?: number; bbox?: string; signal?: AbortSignal }
  > => ({
    mutationKey: [MutationKeys.FETCH_FEATURES_FROM_LAYER],
    mutationFn: async ({ layerName, maxFeatures, bbox, signal }) => {
      // maxFeatures/bbox are optional and bound the response server-side so a
      // 10k+ feature layer doesn't ship its entire GeoJSON for client-side
      // labels/clustering/search. Geometry itself still renders via WMS tiles.
      const response = await axiosInstance.get(ApiRoutes.MAP_FEATURES, {
        params: { layerName, ...(maxFeatures ? { maxFeatures } : {}), ...(bbox ? { bbox } : {}) },
        // A bounded but heavy PostGIS read (ST_AsGeoJSON of thousands of
        // features) can legitimately exceed the default 20s under load /
        // cold connections; give it more headroom so a slow-but-fine response
        // isn't falsely reported as a failure.
        timeout: 45000,
        // Lets the caller cancel a stale request wave (e.g. layers changed
        // again before this resolved) instead of letting it pile up.
        signal,
      });
      return response.data;
    },
    onError: (error) => {
      // Cancelled requests are intentional (superseded wave / unmount).
      if (axios.isCancel(error)) return;
      Notification({
        // Stable key collapses a burst of per-layer failures into a single
        // toast instead of stacking one per layer.
        key: 'fetch-features-error',
        message: mapPortalServiceMessages.FETCH_FEATURES_FROM_LAYER.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const fetchFeaturesKeyAndValueMapping = (layerName?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.FETCH_FEATURES_KEY_VALUE_MAPPING, layerName],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.MAP_FEATURES_MAPPING, {
          params: { layerName },
        });
        return response.data;
      } catch (error) {
        Notification({
          message: mapPortalServiceMessages.FETCH_FEATURES_KEY_VALUE_MAPPING.ERROR,
          description: getAxiosErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!layerName,
  });

  /** Paginated FeatureCollection from /maps/query and /maps/proximity. */
  // Default pageSize 500 matches attribute-table infinite scroll.
  const queryMapFeatures = (): UseMutationOptions<
    {
      type: 'FeatureCollection';
      features: any[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    },
    Error,
    {
      layername: string;
      queryfilter: Record<string, any>;
      // US-103163 search extent: optional GeoJSON Polygon (EPSG:4326).
      extent?: { type: 'Polygon'; coordinates: number[][][] } | null;
      page?: number;
      pageSize?: number;
      includeGeometry?: boolean;
    }
  > => ({
    mutationKey: [MutationKeys.QUERY_MAP_FEATURES],
    mutationFn: async ({ layername, queryfilter, extent, page = 0, pageSize = 500, includeGeometry }) => {
      const response = await axiosInstance.post(ApiRoutes.MAP_QUERY, {
        layername,
        queryfilter,
        ...(extent ? { extent } : {}),
        page,
        pageSize,
        ...(includeGeometry ? { includeGeometry: true } : {}),
      });
      return response.data;
    },
    // US-103168: the Query Tool owns execution-failure UX — it shows the
    // AC-specified inline message ("Unable to execute query…" / "The selected
    // layer is no longer available.") in the panel, so no toast here.
  });

  // US-103032 Proximity analysis: returns one page of matching SOURCE features
  // plus total. The Proximity Tool owns failure UX (inline messages), so no toast.
  const proximityFeatures = (): UseMutationOptions<
    {
      type: 'FeatureCollection';
      features: any[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    },
    Error,
    {
      sourceLayer: string;
      targetLayer: string;
      sourceFilter?: Record<string, any>;
      targetFilter?: Record<string, any>;
      relationship: 'within' | 'outside' | 'closer' | 'farther';
      distance?: number;
      unit?: 'meters' | 'kilometers' | 'miles' | 'feet';
      extent?: { type: 'Polygon'; coordinates: number[][][] } | null;
      page?: number;
      pageSize?: number;
      includeGeometry?: boolean;
    }
  > => ({
    mutationKey: [MutationKeys.PROXIMITY_FEATURES],
    mutationFn: async ({
      sourceLayer,
      targetLayer,
      sourceFilter,
      targetFilter,
      relationship,
      distance,
      unit,
      extent,
      page = 0,
      pageSize = 500,
      includeGeometry,
    }) => {
      const response = await axiosInstance.post(
        ApiRoutes.MAP_PROXIMITY,
        {
          sourceLayer,
          targetLayer,
          ...(sourceFilter && Object.keys(sourceFilter).length ? { sourceFilter } : {}),
          ...(targetFilter && Object.keys(targetFilter).length ? { targetFilter } : {}),
          relationship,
          ...(distance !== undefined ? { distance } : {}),
          ...(unit ? { unit } : {}),
          ...(extent ? { extent } : {}),
          page,
          pageSize,
          ...(includeGeometry ? { includeGeometry: true } : {}),
        },
        {
          timeout: 60000,
        },
      );
      return response.data;
    },
  });

  // US-103163: given the current layers + drawn polygon, returns the subset
  // of layer names that have features inside the extent (to narrow the WITHIN
  // dropdown).
  const fetchLayersInExtent = (): UseMutationOptions<
    string[],
    Error,
    { layerNames: string[]; extent: { type: 'Polygon'; coordinates: number[][][] } }
  > => ({
    mutationKey: ['layersInExtent'],
    mutationFn: async ({ layerNames, extent }) => {
      const response = await axiosInstance.post(ApiRoutes.MAP_LAYERS_IN_EXTENT, {
        layerNames,
        extent,
      });
      return response.data?.layerNames ?? [];
    },
  });

  const editFeatures = (): UseMutationOptions<any, Error, { featureId: number; toolType: string; selectedColumns: any[] }> => ({
    mutationKey: [MutationKeys.EDIT_FEATURES],
    mutationFn: async ({ featureId, toolType, selectedColumns }) => {
      const endpoint = generatePath(ApiRoutes.MAP_FEATURE_BY_ID, {
        featureId: String(featureId),
      });
      const response = await axiosInstance.put(
        endpoint,
        { selectedColumns: { [toolType]: selectedColumns } },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.EDIT_FEATURES.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const editLayerOptions = (): UseMutationOptions<any, Error, { mapId: number; layerId: number; layerOptions: any }> => ({
    mutationKey: [MutationKeys.EDIT_LAYER_OPTIONS],
    mutationFn: async ({ mapId, layerId, layerOptions }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LAYER_BY_ID, {
        mapId: String(mapId),
        layerId: String(layerId),
      });
      const response = await axiosInstance.put(endpoint, layerOptions, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.EDIT_LAYER_OPTIONS.SUCCESS,
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      if (axios.isCancel(error)) return;
      Notification({
        message: mapPortalServiceMessages.EDIT_LAYER_OPTIONS.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getLayerGroups = (mapId?: number): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_LAYER_GROUPS, mapId],
    queryFn: async () => {
      try {
        const endpoint = generatePath(ApiRoutes.MAP_LAYER_GROUPS, {
          mapId: String(mapId),
        });
        const response = await axiosInstance.get(endpoint);
        return response.data;
      } catch (error) {
        Notification({
          message: mapPortalServiceMessages.GET_LAYER_GROUPS.ERROR,
          description: getAxiosErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!mapId,
  });

  const createLayerGroup = (): UseMutationOptions<any, Error, { mapId: number; name: string; layerId: number }> => ({
    mutationKey: [MutationKeys.CREATE_LAYER_GROUP],
    mutationFn: async ({ mapId, name, layerId }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LAYER_GROUPS, {
        mapId: String(mapId),
      });
      const response = await axiosInstance.post(endpoint, { name, layerId });
      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.CREATE_LAYER_GROUP.SUCCESS,
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.CREATE_LAYER_GROUP.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const renameLayerGroup = (): UseMutationOptions<any, Error, { mapId: number; groupId: number; name: string }> => ({
    mutationKey: [MutationKeys.RENAME_LAYER_GROUP],
    mutationFn: async ({ mapId, groupId, name }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LAYER_GROUP_BY_ID, {
        mapId: String(mapId),
        groupId: String(groupId),
      });
      const response = await axiosInstance.patch(endpoint, { name });
      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.RENAME_LAYER_GROUP.SUCCESS,
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.RENAME_LAYER_GROUP.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const ungroupLayerGroup = (): UseMutationOptions<any, Error, { mapId: number; groupId: number }> => ({
    mutationKey: [MutationKeys.UNGROUP_LAYER_GROUP],
    mutationFn: async ({ mapId, groupId }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LAYER_GROUP_BY_ID, {
        mapId: String(mapId),
        groupId: String(groupId),
      });
      const response = await axiosInstance.delete(endpoint);
      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.UNGROUP_LAYER_GROUP.SUCCESS,
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.UNGROUP_LAYER_GROUP.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const reorderLegend = (): UseMutationOptions<
    any,
    Error,
    {
      mapId: number;
      groups?: { id: number; sortOrder: number }[];
      layers?: { id: number; sortOrder: number; groupId?: number | null }[];
    }
  > => ({
    mutationKey: [MutationKeys.REORDER_LEGEND],
    mutationFn: async ({ mapId, groups, layers }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LEGEND_ORDER, {
        mapId: String(mapId),
      });
      const body: Record<string, unknown> = {};
      if (groups && groups.length) body.groups = groups;
      if (layers && layers.length) body.layers = layers;
      const response = await axiosInstance.put(endpoint, body);
      return response.data;
    },
    // Toast is intentionally suppressed on success — reorder fires once per
    // drop and a notification every time would be noisy. Errors still toast.
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.REORDER_LEGEND.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const editGeoServerFeatures = (): UseMutationOptions<any, Error, { layerName: string; payload: any }> => ({
    mutationKey: [MutationKeys.EDIT_GEOSERVER_FEATURES],
    mutationFn: async ({ layerName, payload }) => {
      const endpoint = generatePath(ApiRoutes.MAP_LAYER_FEATURES_UPDATE, {
        layerName,
      });
      const response = await axiosInstance.put(
        endpoint,
        { payload },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.EDIT_GEOSERVER_FEATURES.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const uploadFileToFolder = (): UseMutationOptions<any, Error, { payload: any }> => ({
    mutationKey: [MutationKeys.UPLOAD_FILE_TO_FOLDER],
    mutationFn: async ({ payload }) => {
      const { data } = await axiosInstance.post(`${ApiRoutes.API_BASE_URL}${ApiRoutes.FILE_UPLOAD}`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    },
    onSuccess: (data) => {
      if (data?.success) {
        Notification({
          message: data?.message || mapPortalServiceMessages.UPLOAD_FILE_TO_FOLDER.SUCCESS_FALLBACK,
          type: NotificationTypes.SUCCESS,
        });
      }
    },
    onError: (error) => {
      const axiosError = error as AxiosError<any>;
      Notification({
        message: axiosError.response?.data?.message || mapPortalServiceMessages.UPLOAD_FILE_TO_FOLDER.ERROR_FALLBACK,
        type: NotificationTypes.ERROR,
      });
    },
  });

  // US-101366 — create a new folder for the "Save in New Folder" path. The
  // returned folder.folder_id is the value to send as properties.folderId
  // on the subsequent uploadFeaturesToFolder call.
  const createFilemanagerFolder = (): UseMutationOptions<any, Error, { folderName: string; currentDirectoryId: string }> => ({
    mutationKey: [MutationKeys.CREATE_FILEMANAGER_FOLDER],
    mutationFn: async ({ folderName, currentDirectoryId }) => {
      const response = await axiosInstance.post(ApiRoutes.FILEMANAGER_CREATE_FOLDER, {
        folderName,
        currentDirectoryId,
      });
      return response.data;
    },
    onError: (error) => {
      const axiosError = error as AxiosError<any>;
      Notification({
        message: axiosError.response?.data?.message || 'Unable to create folder',
        type: NotificationTypes.ERROR,
      });
    },
  });

  const uploadFeaturesToFolder = (): UseMutationOptions<any, Error, { payload: any }> => ({
    mutationKey: [MutationKeys.UPLOAD_FEATURES_TO_FOLDER],
    mutationFn: async ({ payload }) => {
      const { data } = await axiosInstance.post(`${ApiRoutes.API_BASE_URL}${ApiRoutes.FEATURES_UPLOAD}`, payload);

      return data;
    },
    onSuccess: (data) => {
      // Now async: the API returns a jobId and the export runs on the worker.
      if (data?.jobId) {
        Notification({
          message: 'Saving to the file manager — it will appear shortly.',
          type: NotificationTypes.INFO,
        });
      } else if (data?.success) {
        Notification({
          message: data?.message || mapPortalServiceMessages.UPLOAD_FEATURES_TO_FOLDER.SUCCESS_FALLBACK,
          type: NotificationTypes.SUCCESS,
        });
      }
    },
    onError: (error) => {
      const axiosError = error as AxiosError<any>;
      Notification({
        message: axiosError.response?.data?.message || mapPortalServiceMessages.UPLOAD_FEATURES_TO_FOLDER.ERROR_FALLBACK,
        type: NotificationTypes.ERROR,
      });
    },
  });

  const createLayerFromGeoJSON = (): UseMutationOptions<any, Error, { payload: any }> => ({
    mutationKey: [MutationKeys.CREATE_LAYER_FROM_GEOJSON],
    mutationFn: async ({ payload }) => {
      const { data } = await axiosInstance.post(`${ApiRoutes.API_BASE_URL}${ApiRoutes.FILE_SAVE_LAYER}`, payload);

      return data;
    },
    onSuccess: (data) => {
      if (data?.success) {
        Notification({
          message: data.message || mapPortalServiceMessages.CREATE_LAYER_FROM_GEOJSON.SUCCESS_FALLBACK,
          type: NotificationTypes.SUCCESS,
        });
      }
    },
    onError: (error) => {
      const axiosError = error as AxiosError<any>;
      Notification({
        message: axiosError.response?.data?.message || mapPortalServiceMessages.CREATE_LAYER_FROM_GEOJSON.ERROR_FALLBACK,
        type: NotificationTypes.ERROR,
      });
    },
  });

  const createLayerFromFeatures = (): UseMutationOptions<any, Error, { payload: any }> => ({
    mutationKey: [MutationKeys.CREATE_LAYER_FROM_FEATURES],
    mutationFn: async ({ payload }) => {
      const { data } = await axiosInstance.post(`${ApiRoutes.API_BASE_URL}${ApiRoutes.FILE_SAVE_FEATURES}`, payload);

      return data;
    },
    onSuccess: (data) => {
      // Now async: the API returns { jobId, status: 'processing' } and the layer
      // is materialized on the worker, then appears (and auto-zooms) on the map.
      if (data?.jobId || data?.status === 'processing') {
        Notification({
          message: 'Creating layer — it will appear on the map shortly.',
          type: NotificationTypes.INFO,
        });
      } else if (data?.success) {
        Notification({
          message: data.message || mapPortalServiceMessages.CREATE_LAYER_FROM_FEATURES.SUCCESS_FALLBACK,
          type: NotificationTypes.SUCCESS,
        });
      }
    },
    onError: (error) => {
      const axiosError = error as AxiosError<any>;
      Notification({
        message: axiosError.response?.data?.message || mapPortalServiceMessages.CREATE_LAYER_FROM_FEATURES.ERROR_FALLBACK,
        type: NotificationTypes.ERROR,
      });
    },
  });

  // Create Feature Tool (Sprint 4) ------------------------------------------

  // Existing layers in the map that match the chosen geometry type, with their
  // read-only schema, for "append to existing layer" (US-104111).
  const getFeatureLayers = (mapId?: string, geometryType?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_FEATURE_LAYERS, mapId, geometryType],
    queryFn: async () => {
      const endpoint = generatePath(ApiRoutes.MAP_FEATURE_LAYERS, { mapId: String(mapId) });
      const { data } = await axiosInstance.get(endpoint, { params: { geometryType } });
      return data?.layers ?? [];
    },
    enabled: !!mapId,
  });

  // Create + publish a new empty layer (US-104110/109003/109004).
  const createFeatureLayer = (): UseMutationOptions<
    any,
    Error,
    { mapId: string; layerName: string; geometryType: string; fields: { name: string; dataType: string }[] }
  > => ({
    mutationKey: [MutationKeys.CREATE_FEATURE_LAYER],
    mutationFn: async ({ mapId, layerName, geometryType, fields }) => {
      const endpoint = generatePath(ApiRoutes.MAP_FEATURE_LAYERS, { mapId });
      const { data } = await axiosInstance.post(endpoint, { layerName, geometryType, fields });
      return data;
    },
    onSuccess: (data) => {
      Notification({
        message: data?.message || 'Layer created successfully.',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: getAxiosErrorMessage(error) || 'Unable to create layer. Please try again.',
        type: NotificationTypes.ERROR,
      });
    },
  });

  // Save a feature (geometry + attributes + optional JPG/PNG attachments) to a
  // layer (US-104113/108780/109002). Sends multipart when files are present.
  const createFeature = (): UseMutationOptions<
    any,
    Error,
    { layerName: string; geometry: unknown; properties?: Record<string, unknown>; attachments?: File[] }
  > => ({
    mutationKey: [MutationKeys.CREATE_FEATURE],
    mutationFn: async ({ layerName, geometry, properties, attachments }) => {
      const endpoint = generatePath(ApiRoutes.MAP_CREATE_FEATURE, { layerName });
      if (attachments?.length) {
        const fd = new FormData();
        fd.append('geometry', JSON.stringify(geometry));
        fd.append('properties', JSON.stringify(properties ?? {}));
        attachments.forEach((file) => fd.append('attachments', file));
        const { data } = await axiosInstance.post(endpoint, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
      }
      const { data } = await axiosInstance.post(endpoint, { geometry, properties: properties ?? {} });
      return data;
    },
    onSuccess: (data) => {
      Notification({
        message: data?.message || 'Feature saved successfully.',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: getAxiosErrorMessage(error) || 'Unable to save feature. Please try again.',
        type: NotificationTypes.ERROR,
      });
    },
  });

  // Edit an existing saved feature's geometry/attributes (US-108778/108780).
  const updateFeature = (): UseMutationOptions<
    any,
    Error,
    { layerName: string; gid: number; geometry?: unknown; properties?: Record<string, unknown> }
  > => ({
    mutationKey: [MutationKeys.UPDATE_FEATURE],
    mutationFn: async ({ layerName, gid, geometry, properties }) => {
      const endpoint = generatePath(ApiRoutes.MAP_UPDATE_FEATURE, { layerName, gid: String(gid) });
      const { data } = await axiosInstance.put(endpoint, { geometry, properties });
      return data;
    },
    onSuccess: (data) => {
      Notification({ message: data?.message || 'Feature saved successfully.', type: NotificationTypes.SUCCESS });
    },
    onError: (error) => {
      Notification({
        message: getAxiosErrorMessage(error) || 'Unable to save feature. Please try again.',
        type: NotificationTypes.ERROR,
      });
    },
  });

  // Permanently delete a saved feature (US-108778 delete control).
  const deleteFeature = (): UseMutationOptions<any, Error, { layerName: string; gid: number }> => ({
    mutationKey: [MutationKeys.DELETE_FEATURE],
    mutationFn: async ({ layerName, gid }) => {
      const endpoint = generatePath(ApiRoutes.MAP_UPDATE_FEATURE, { layerName, gid: String(gid) });
      const { data } = await axiosInstance.delete(endpoint);
      return data;
    },
    onSuccess: () => {
      Notification({ message: 'Feature deleted.', type: NotificationTypes.SUCCESS });
    },
    onError: (error) => {
      Notification({
        message: getAxiosErrorMessage(error) || 'Unable to delete feature. Please try again.',
        type: NotificationTypes.ERROR,
      });
    },
  });

  // Nearest feature to a clicked lng/lat — opens an existing feature for editing
  // (US-108778). Tolerant PostGIS lookup; returns null if nothing is in range.
  const getFeatureAt = (): UseMutationOptions<any, Error, { layerName: string; lng: number; lat: number; tol: number }> => ({
    mutationKey: ['getFeatureAt'],
    mutationFn: async ({ layerName, lng, lat, tol }) => {
      const endpoint = generatePath(ApiRoutes.MAP_FEATURE_AT, { layerName });
      const { data } = await axiosInstance.get(endpoint, { params: { lng, lat, tol } });
      return data?.feature ?? null;
    },
  });

  // Existing attachments for a feature (shown when editing). US-109002.
  const getFeatureAttachments = (layerName: string, gid?: number): UseQueryOptions<any, Error> => ({
    queryKey: ['featureAttachments', layerName, gid],
    queryFn: async () => {
      const endpoint = generatePath(ApiRoutes.MAP_FEATURE_ATTACHMENTS, { layerName, gid: String(gid) });
      const { data } = await axiosInstance.get(endpoint);
      return data?.attachments ?? [];
    },
    enabled: !!layerName && gid != null,
  });

  // Add attachments to an already-saved feature (multipart). US-109002.
  const uploadFeatureAttachments = (): UseMutationOptions<any, Error, { layerName: string; gid: number; attachments: File[] }> => ({
    mutationKey: [MutationKeys.UPLOAD_FEATURE_ATTACHMENTS],
    mutationFn: async ({ layerName, gid, attachments }) => {
      const endpoint = generatePath(ApiRoutes.MAP_FEATURE_ATTACHMENTS, { layerName, gid: String(gid) });
      const fd = new FormData();
      attachments.forEach((file) => fd.append('attachments', file));
      const { data } = await axiosInstance.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onError: (error) => {
      Notification({
        message: getAxiosErrorMessage(error) || 'Unable to upload attachments.',
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getMapSketches = (mapId?: number): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_MAP_SKETCHES, mapId],
    queryFn: async () => {
      try {
        const endpoint = generatePath(ApiRoutes.MAP_SKETCHES, {
          mapId: String(mapId),
        });
        const response = await axiosInstance.get(endpoint);
        return response.data;
      } catch (error) {
        Notification({
          message: mapPortalServiceMessages.GET_MAP_SKETCHES.ERROR,
          description: getAxiosErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!mapId,
  });

  const createMapSketch = (): UseMutationOptions<any, Error, { sketchData: Sketch }> => ({
    mutationKey: [MutationKeys.CREATE_MAP_SKETCH],
    mutationFn: async ({ sketchData }) => {
      const endpoint = generatePath(ApiRoutes.MAP_SKETCHES, {
        mapId: String(sketchData.mapId),
      });
      const response = await axiosInstance.post(endpoint, sketchData);
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.CREATE_MAP_SKETCH.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getMapSketch = (id?: number): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_MAP_SKETCH, id],
    queryFn: async () => {
      try {
        const endpoint = generatePath(ApiRoutes.MAP_SKETCHES, {
          mapId: String(id),
        });
        const response = await axiosInstance.get(endpoint);
        return response.data;
      } catch (error) {
        Notification({
          message: mapPortalServiceMessages.GET_MAP_SKETCH.ERROR,
          description: getAxiosErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!id,
  });

  const editMapSketch = (): UseMutationOptions<any, Error, { sketchData: Sketch }> => ({
    mutationKey: [MutationKeys.EDIT_MAP_SKETCH],
    mutationFn: async ({ sketchData }) => {
      const endpoint = generatePath(ApiRoutes.MAP_SKETCH_BY_MAP, {
        mapId: String(sketchData.mapId),
        sketchId: String(sketchData.id),
      });
      const response = await axiosInstance.put(endpoint, sketchData);
      return response.data;
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.EDIT_MAP_SKETCH.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const deleteMapSketch = (): UseMutationOptions<boolean, Error, { sketchId: string }> => ({
    mutationKey: [MutationKeys.DELETE_MAP_SKETCH],
    mutationFn: async ({ sketchId }) => {
      const endpoint = generatePath(ApiRoutes.MAP_SKETCH_BY_ID, {
        sketchId: String(sketchId),
      });
      await axiosInstance.delete(endpoint);
      return true;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.DELETE_MAP_SKETCH.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.DELETE_MAP_SKETCH.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const addSketchItems = (): UseMutationOptions<any, Error, { sketchId: number; items: SketchItem[] }> => ({
    mutationKey: [MutationKeys.ADD_SKETCH_ITEMS],
    mutationFn: async ({ sketchId, items }) => {
      const endpoint = generatePath(ApiRoutes.MAP_SKETCH_ITEMS, {
        sketchId: String(sketchId),
      });
      const normalizedItems = items.map(normalizeSketchItemPayload);
      const response = await axiosInstance.post(endpoint, { items: normalizedItems });

      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.ADD_SKETCH_ITEMS.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.ADD_SKETCH_ITEMS.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const editSketchItem = (): UseMutationOptions<any, Error, { id: number; payload: SketchItem }> => ({
    mutationKey: [MutationKeys.EDIT_SKETCH_ITEM],
    mutationFn: async ({ id, payload }) => {
      const endpoint = generatePath(ApiRoutes.MAP_SKETCH_ITEM, {
        id: String(id),
      });
      const normalizedPayload = normalizeSketchItemPayload(payload);
      const response = await axiosInstance.put(endpoint, normalizedPayload);

      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.EDIT_SKETCH_ITEM.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.EDIT_SKETCH_ITEM.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const deleteSketchItem = (): UseMutationOptions<boolean, Error, { id: number }> => ({
    mutationKey: [MutationKeys.DELETE_SKETCH_ITEM],
    mutationFn: async ({ id }) => {
      const endpoint = generatePath(ApiRoutes.MAP_SKETCH_ITEM, {
        id: String(id),
      });
      await axiosInstance.delete(endpoint);
      return true;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.DELETE_SKETCH_ITEM.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.DELETE_SKETCH_ITEM.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const saveSketch = (): UseMutationOptions<any, Error, { sketchId: string; format: string }> => ({
    mutationKey: [MutationKeys.SAVE_SKETCH],
    mutationFn: async ({ sketchId, format }) => {
      const endpoint = generatePath(ApiRoutes.MAP_SKETCH_SAVE, {
        sketchId: String(sketchId),
      });
      const response = await axiosInstance.post(endpoint, { format }, { responseType: 'blob' });

      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: mapPortalServiceMessages.SAVE_SKETCH.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: mapPortalServiceMessages.SAVE_SKETCH.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  return {
    createMap,
    getAllMaps,
    getMapById,
    getMapLayers,
    getLayerBounds,
    getLayerBoundsQuery,
    createDuplicateLayer,
    downloadLayer,
    downloadSelectedFeatures,
    fetchFeaturesFromLayer,
    editMap,
    fetchFeaturesKeyAndValueMapping,
    getFeatureTable,
    updateFeatureTableRows,
    getFeatureCqlForPredicate,
    requestFeatureExport,
    getFeatureExportStatus,
    getLayerSymbologyFields,
    getFieldDistinctValues,
    getFieldValueSample,
    queryMapFeatures,
    proximityFeatures,
    fetchLayersInExtent,
    editFeatures,
    deleteMap,
    editLayerOptions,
    getLayerGroups,
    createLayerGroup,
    renameLayerGroup,
    ungroupLayerGroup,
    reorderLegend,
    deleteLayer,
    editGeoServerFeatures,
    uploadFileToFolder,
    createLayerFromGeoJSON,
    createLayerFromFeatures,
    getFeatureLayers,
    createFeatureLayer,
    createFeature,
    updateFeature,
    deleteFeature,
    getFeatureAt,
    getFeatureAttachments,
    uploadFeatureAttachments,
    uploadFeaturesToFolder,
    createFilemanagerFolder,
    getMapSketches,
    createMapSketch,
    getMapSketch,
    editMapSketch,
    deleteMapSketch,
    addSketchItems,
    editSketchItem,
    deleteSketchItem,
    saveSketch,
  };
};

export default MapPortalService;
